import { View, ViewDensity, ViewType } from "@common/types/views/views";
import { ensureViewColumn } from "@lib/mergeCachedView";
import { AccrualMatchColumn } from "./types";

export interface AccrualMatchDefaultViewOptions {
    /**
     * Non-GL-first ("category-first") workspaces classify transactions by
     * category and show a dedicated Category column; GL-first workspaces
     * classify by GL account and omit it.
     */
    isGlFirst: boolean;
    /**
     * Whether the transaction being matched has an accrual ledger.
     * Dual-ledger entities show both Cash ledger and Accrual ledger;
     * cash-only entities show Cash ledger alone.
     */
    hasAccrualLedger: boolean;
    /** The `schedules` feature flag; adds the Schedule column when on. */
    isSchedulesEnabled?: boolean;
}

/**
 * Builds the drawer's default column set (PRO-12048). The Cash ledger and
 * Accrual ledger columns replace the legacy single `ACCOUNT` column (which
 * conflated Category/Cash ledger/Accrual ledger into one basis-swapped
 * column and never showed cash + accrual side by side). Class is
 * deliberately excluded from the default set in every case — it remains
 * available via column settings (Views), not shown by default.
 */
export function getDefaultAccrualMatchView({
    isGlFirst,
    hasAccrualLedger,
    isSchedulesEnabled = false,
}: AccrualMatchDefaultViewOptions): View {
    return {
        type: ViewType.TRANSACTION,
        columns: [
            { id: AccrualMatchColumn.LOCK, options: { pin: "left" } },
            { id: AccrualMatchColumn.DATE },
            { id: AccrualMatchColumn.TYPE },
            { id: AccrualMatchColumn.COUNTERPARTY },
            { id: AccrualMatchColumn.DESCRIPTION },
            ...(isGlFirst ? [] : [{ id: AccrualMatchColumn.CATEGORY }]),
            { id: AccrualMatchColumn.CASH_LEDGER },
            ...(hasAccrualLedger
                ? [{ id: AccrualMatchColumn.ACCRUAL_LEDGER }]
                : []),
            ...(isSchedulesEnabled
                ? [{ id: AccrualMatchColumn.SCHEDULE }]
                : []),
            { id: AccrualMatchColumn.FINANCIAL_ACCOUNT },
            { id: AccrualMatchColumn.AMOUNT },
            { id: AccrualMatchColumn.ENTITY },
            { id: AccrualMatchColumn.OPEN, options: { pin: "right" } },
            { id: AccrualMatchColumn.ACTION, options: { pin: "right" } },
        ],
        filters: {},
        options: { density: ViewDensity.MD },
    };
}

/**
 * Legacy default column set: a single basis-swapped `ACCOUNT` column
 * (Category, Cash ledger, or Accrual ledger depending on workspace/entity
 * config — see `createAccrualMatchViewConfig`) instead of the dedicated
 * Category/Cash ledger/Accrual ledger columns `getDefaultAccrualMatchView`
 * above builds. Kept for:
 * - the reverse-match drawer (invoice/bill → transaction), which the
 *   PRO-12048 redesign doesn't cover;
 * - the view context's initial value, read only until a real provider
 *   mounts.
 */
export const defaultAccrualMatchView: View = {
    type: ViewType.TRANSACTION,
    columns: [
        { id: AccrualMatchColumn.DATE },
        { id: AccrualMatchColumn.TYPE },
        { id: AccrualMatchColumn.COUNTERPARTY },
        { id: AccrualMatchColumn.DESCRIPTION },
        { id: AccrualMatchColumn.ACCOUNT },
        { id: AccrualMatchColumn.CLASS },
        { id: AccrualMatchColumn.FINANCIAL_ACCOUNT },
        { id: AccrualMatchColumn.AMOUNT },
        { id: AccrualMatchColumn.ENTITY },
        { id: AccrualMatchColumn.OPEN, options: { pin: "right" } },
        { id: AccrualMatchColumn.ACTION, options: { pin: "right" } },
    ],
    filters: {},
    options: { density: ViewDensity.MD },
};

/** The legacy column set plus the Schedule column when the flag is on. */
export function getLegacyAccrualMatchView(options: {
    isSchedulesEnabled: boolean;
}): View {
    return options.isSchedulesEnabled
        ? ensureViewColumn(defaultAccrualMatchView, AccrualMatchColumn.SCHEDULE)
        : defaultAccrualMatchView;
}

function viewHasColumn(view: View, columnId: string): boolean {
    return view.columns.some((column) => column.id === columnId);
}

function insertColumnBefore<T extends View>(
    view: T,
    columnId: string,
    beforeId: string,
): T {
    const beforeIndex = view.columns.findIndex(
        (column) => column.id === beforeId,
    );
    if (beforeIndex === -1) {
        return ensureViewColumn(view, columnId);
    }
    return {
        ...view,
        columns: [
            ...view.columns.slice(0, beforeIndex),
            { id: columnId },
            ...view.columns.slice(beforeIndex),
        ],
    };
}

function placeColumnAfter<T extends View>(
    view: T,
    columnId: string,
    afterId: string,
): T {
    const afterIndex = view.columns.findIndex(
        (column) => column.id === afterId,
    );
    const columnIndex = view.columns.findIndex(
        (column) => column.id === columnId,
    );
    if (afterIndex === -1) {
        return columnIndex === -1 ? ensureViewColumn(view, columnId) : view;
    }
    if (columnIndex === afterIndex + 1) {
        return view;
    }
    const existingColumn = view.columns[columnIndex];
    const column =
        columnIndex === -1 || !existingColumn
            ? { id: columnId }
            : existingColumn;
    const withoutColumn = view.columns.filter((entry) => entry.id !== columnId);
    const insertionIndex =
        withoutColumn.findIndex((entry) => entry.id === afterId) + 1;
    return {
        ...view,
        columns: [
            ...withoutColumn.slice(0, insertionIndex),
            column,
            ...withoutColumn.slice(insertionIndex),
        ],
    };
}

/**
 * Adds the cash column (Income account on invoices, Expense account on bills)
 * and, when the transaction side has an accrual ledger, the accrual column.
 * Each is offered once, so a later hide stays hidden. Whenever both are in the
 * view they sit next to each other, accrual immediately after cash.
 */
export function enforceLedgerColumns<T extends View>(options: {
    view: T;
    hasAccrualLedger: boolean;
    wasCashOffered: boolean;
    wasAccrualOffered: boolean;
}): T {
    let next = options.view;
    if (
        !options.wasCashOffered &&
        !viewHasColumn(next, AccrualMatchColumn.CASH_LEDGER)
    ) {
        next = viewHasColumn(next, AccrualMatchColumn.ACCRUAL_LEDGER)
            ? insertColumnBefore(
                  next,
                  AccrualMatchColumn.CASH_LEDGER,
                  AccrualMatchColumn.ACCRUAL_LEDGER,
              )
            : ensureViewColumn(next, AccrualMatchColumn.CASH_LEDGER);
    }
    if (
        options.hasAccrualLedger &&
        !options.wasAccrualOffered &&
        !viewHasColumn(next, AccrualMatchColumn.ACCRUAL_LEDGER)
    ) {
        next = placeColumnAfter(
            next,
            AccrualMatchColumn.ACCRUAL_LEDGER,
            AccrualMatchColumn.CASH_LEDGER,
        );
    }
    if (
        viewHasColumn(next, AccrualMatchColumn.CASH_LEDGER) &&
        viewHasColumn(next, AccrualMatchColumn.ACCRUAL_LEDGER)
    ) {
        next = placeColumnAfter(
            next,
            AccrualMatchColumn.ACCRUAL_LEDGER,
            AccrualMatchColumn.CASH_LEDGER,
        );
    }
    return next;
}

/**
 * A view cached before the Schedule column existed would never show it, so
 * the drawer adds it once while the flag is on. After that a hidden column
 * stays hidden: views record hidden columns by leaving them out.
 */
export function offerScheduleColumnOnce<T extends View>(options: {
    view: T;
    isSchedulesEnabled: boolean;
    wasOffered: boolean;
}): T {
    return options.isSchedulesEnabled && !options.wasOffered
        ? ensureViewColumn(options.view, AccrualMatchColumn.SCHEDULE)
        : options.view;
}
